import {
  Apple,
  ArrowDownLeft,
  ArrowUpRight,
  Baby,
  BadgeRussianRuble,
  Banknote,
  BookOpen,
  Briefcase,
  Bus,
  Car,
  Cigarette,
  Circle,
  CircleDollarSign,
  Clapperboard,
  Coffee,
  CreditCard,
  Dumbbell,
  Fuel,
  Gamepad2,
  Gift,
  GraduationCap,
  HandCoins,
  Heart,
  House,
  Landmark,
  Laptop,
  Music,
  PawPrint,
  Percent,
  PiggyBank,
  Plane,
  ReceiptText,
  Redo2,
  Scissors,
  Shield,
  Shirt,
  ShoppingBag,
  ShoppingBasket,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Stethoscope,
  Target,
  Ticket,
  TrendingUp,
  Undo2,
  Users,
  Utensils,
  WalletCards,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export const financeIconChoices = [
  ['briefcase', 'Работа'],
  ['wallet', 'Кошелёк'],
  ['credit-card', 'Карта'],
  ['banknote', 'Наличные'],
  ['landmark', 'Банк'],
  ['piggy-bank', 'Накопления'],
  ['ruble', 'Рубли'],
  ['coins', 'Деньги'],
  ['growth', 'Инвестиции'],
  ['percent', 'Проценты'],
  ['home', 'Дом'],
  ['car', 'Авто'],
  ['fuel', 'Топливо'],
  ['bus', 'Транспорт'],
  ['plane', 'Путешествия'],
  ['shopping-cart', 'Продукты'],
  ['shopping-bag', 'Покупки'],
  ['utensils', 'Кафе'],
  ['coffee', 'Кофе'],
  ['apple', 'Еда'],
  ['health', 'Здоровье'],
  ['fitness', 'Спорт'],
  ['beauty', 'Красота'],
  ['clothes', 'Одежда'],
  ['receipt', 'Счета'],
  ['phone', 'Телефон'],
  ['laptop', 'Техника'],
  ['cinema', 'Кино'],
  ['game', 'Игры'],
  ['music', 'Музыка'],
  ['book', 'Образование'],
  ['gift', 'Подарки'],
  ['family', 'Семья'],
  ['baby', 'Дети'],
  ['pets', 'Питомцы'],
  ['education', 'Учёба'],
  ['tools', 'Ремонт'],
  ['scissors', 'Услуги'],
  ['ticket', 'Билеты'],
  ['shield', 'Страхование'],
  ['target', 'Цель'],
  ['smoking', 'Курилка'],
  ['heart', 'Личное'],
  ['hand-coins', 'Долги'],
  ['transfer-in', 'Поступление'],
  ['transfer-out', 'Списание'],
  ['return-in', 'Возврат'],
  ['return-out', 'Возврат долга'],
  ['other', 'Другое'],
] as const;

const financeIcons: Record<string, LucideIcon> = {
  apple: Apple,
  baby: Baby,
  banknote: Banknote,
  basket: ShoppingBasket,
  beauty: Sparkles,
  book: BookOpen,
  briefcase: Briefcase,
  bus: Bus,
  car: Car,
  cash: Banknote,
  cinema: Clapperboard,
  clothes: Shirt,
  coffee: Coffee,
  coins: CircleDollarSign,
  'credit-card': CreditCard,
  education: GraduationCap,
  family: Users,
  fitness: Dumbbell,
  fuel: Fuel,
  game: Gamepad2,
  gift: Gift,
  growth: TrendingUp,
  'hand-coins': HandCoins,
  health: Stethoscope,
  heart: Heart,
  home: House,
  landmark: Landmark,
  laptop: Laptop,
  music: Music,
  other: Circle,
  percent: Percent,
  pets: PawPrint,
  'piggy-bank': PiggyBank,
  phone: Smartphone,
  plane: Plane,
  receipt: ReceiptText,
  'return-in': Undo2,
  'return-out': Redo2,
  ruble: BadgeRussianRuble,
  scissors: Scissors,
  shield: Shield,
  shirt: Shirt,
  'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart,
  smoking: Cigarette,
  target: Target,
  ticket: Ticket,
  tools: Wrench,
  'transfer-in': ArrowDownLeft,
  'transfer-out': ArrowUpRight,
  utensils: Utensils,
  wallet: WalletCards,
};

const legacyFinanceIconAliases: Record<string, string> = {
  '💼': 'briefcase',
  '🧑‍💻': 'laptop',
  '📈': 'growth',
  '🚙': 'car',
  '🚗': 'car',
  '🍽️': 'utensils',
  '🩺': 'health',
  '🧾': 'receipt',
  '🏠': 'home',
  '☕': 'smoking',
  '💜': 'heart',
  '👕': 'clothes',
  '🎁': 'gift',
  '🥑': 'shopping-cart',
  '✈️': 'plane',
  '🎬': 'cinema',
  '💳': 'credit-card',
  '👨‍👩‍👧': 'family',
  '🎯': 'target',
  '💰': 'coins',
  '🛍️': 'shopping-bag',
  '🛒️': 'shopping-cart',
  '↙️': 'transfer-in',
  '↗️': 'transfer-out',
  '🤝': 'hand-coins',
  '🤲': 'hand-coins',
  '↩️': 'return-in',
  '↪️': 'return-out',
  '•': 'other',
};

export function normalizeFinanceIconId(
  name?: string,
  fallback = 'wallet',
): string {
  const normalized = name ? legacyFinanceIconAliases[name] ?? name : fallback;
  return financeIcons[normalized] === undefined ? fallback : normalized;
}

export function getFinanceIcon(
  name?: string,
  fallback = 'wallet',
): LucideIcon {
  return financeIcons[normalizeFinanceIconId(name, fallback)] ?? WalletCards;
}

export function getBudgetFinanceIconId(name?: string, icon?: string): string {
  if (icon) return normalizeFinanceIconId(icon, 'wallet');
  const normalized = name?.toLocaleLowerCase('ru-RU') ?? '';
  if (normalized.includes('налич')) return 'banknote';
  if (normalized.includes('вклад') || normalized.includes('сбер')) return 'landmark';
  if (normalized.includes('инвест')) return 'growth';
  if (normalized.includes('ип') || normalized.includes('работ')) return 'briefcase';
  if (normalized.includes('отпуск') || normalized.includes('путеш')) return 'plane';
  if (normalized.includes('сем')) return 'family';
  return 'credit-card';
}
